import * as React from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Warning as WarningIcon } from "@phosphor-icons/react/Warning";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ConfirmationModalItem {
	id: string;
	title: string;
	subtitle?: string;
	metadata?: Record<string, any>;
}

export interface ConfirmationModalAction {
	label: string;
	color?:
		| "inherit"
		| "primary"
		| "secondary"
		| "success"
		| "error"
		| "info"
		| "warning";
	variant?: "text" | "outlined" | "contained";
	onClick: () => void;
}

export interface ConfirmationModalProps {
	/** Whether the dialog is open */
	open: boolean;
	/** Dialog title */
	title: string;
	/** Dialog description/message - legacy 'content' also supported */
	content?: string;
	message?: string;
	/** Callback when dialog should close */
	onClose: () => void;
	/** Callback when primary action is confirmed - legacy support */
	onConfirm?: () => void;
	/** Primary action button text - legacy support */
	actionText?: string;
	/** Whether the operation is in progress */
	loading?: boolean;
	/** Error message to display */
	error?: string | null;
	/** Icon severity - legacy support */
	severity?: "warning" | "error";
	/** List of items to be affected (for bulk operations) */
	items?: ConfirmationModalItem[];
	/** Maximum items to display in preview (default: 10) */
	maxItemsPreview?: number;
	/** Icon to display in dialog */
	icon?: React.ReactNode;
	/** Icon color for avatar background */
	iconColor?: "default" | "error" | "warning" | "info" | "success";
	/** Primary action (typically confirm) */
	primaryAction?: ConfirmationModalAction;
	/** Secondary action (typically cancel) */
	secondaryAction?: ConfirmationModalAction;
	/** Additional actions */
	additionalActions?: ConfirmationModalAction[];
	/** Custom content to render in dialog body */
	children?: React.ReactNode;
	/** Whether to prevent closing when loading */
	preventCloseOnLoading?: boolean;
	/** Custom styles */
	sx?: any;
}

export function ConfirmationModal({
	open,
	title,
	content,
	message,
	onClose,
	onConfirm,
	actionText = "Confirm",
	loading = false,
	error = null,
	severity = "warning",
	items = [],
	maxItemsPreview = 10,
	icon,
	iconColor,
	primaryAction,
	secondaryAction,
	additionalActions = [],
	children,
	preventCloseOnLoading = true,
	sx,
}: ConfirmationModalProps): React.JSX.Element {
	const canClose = !loading || !preventCloseOnLoading;

	// Determine the message to display (new 'message' prop takes precedence over legacy 'content')
	const displayMessage = message ?? content ?? "";

	// Determine icon color (new iconColor prop takes precedence over legacy severity)
	const effectiveIconColor =
		iconColor ?? (severity === "error" ? "error" : "warning");

	// Build actions array - legacy API creates primary and secondary actions
	const legacyPrimaryAction: ConfirmationModalAction | undefined = onConfirm
		? {
				label: actionText,
				color: severity === "error" ? "error" : "warning",
				variant: "contained",
				onClick: onConfirm,
			}
		: undefined;

	const legacySecondaryAction: ConfirmationModalAction = {
		label: "Cancel",
		color: "secondary",
		variant: "text",
		onClick: onClose,
	};

	// Use new actions if provided, otherwise fall back to legacy actions
	const allActions =
		primaryAction || secondaryAction
			? [
					...(secondaryAction ? [secondaryAction] : [legacySecondaryAction]),
					...additionalActions,
					...(primaryAction ? [primaryAction] : []),
				]
			: [
					legacySecondaryAction,
					...(legacyPrimaryAction ? [legacyPrimaryAction] : []),
				];

	const handleClose = () => {
		if (canClose) {
			onClose();
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Escape" && canClose) {
			onClose();
		}
	};

	const getIconColorStyles = (color: typeof effectiveIconColor) => {
		switch (color) {
			case "error":
				return {
					bgcolor: "var(--mui-palette-error-50)",
					color: "var(--mui-palette-error-main)",
				};
			case "warning":
				return {
					bgcolor: "var(--mui-palette-warning-50)",
					color: "var(--mui-palette-warning-main)",
				};
			case "info":
				return {
					bgcolor: "var(--mui-palette-info-50)",
					color: "var(--mui-palette-info-main)",
				};
			case "success":
				return {
					bgcolor: "var(--mui-palette-success-50)",
					color: "var(--mui-palette-success-main)",
				};
			default:
				return {
					bgcolor: "var(--mui-palette-action-selected)",
					color: "var(--mui-palette-text-primary)",
				};
		}
	};

	const defaultIcon = icon ?? <WarningIcon fontSize="var(--Icon-fontSize)" />;
	const itemCount = items.length;
	const hasItemsPreview = itemCount > 0;
	const showAllItems = itemCount <= maxItemsPreview;
	const visibleItems = showAllItems ? items : items.slice(0, maxItemsPreview);
	const hiddenItemsCount = itemCount - maxItemsPreview;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				onKeyDown: handleKeyDown,
			}}
			sx={{
				"& .MuiDialog-paper": {
					bgcolor: "transparent",
					boxShadow: "none",
					overflow: "visible",
				},
				...sx,
			}}>
			<Container maxWidth="sm">
				<Paper
					sx={{
						border: "1px solid var(--mui-palette-divider)",
						boxShadow: "var(--mui-shadows-16)",
					}}>
					<Stack
						direction="row"
						spacing={2}
						sx={{ display: "flex", p: 3 }}>
						{/* Icon */}
						<Avatar
							sx={{
								...getIconColorStyles(effectiveIconColor),
								flexShrink: 0,
							}}>
							{defaultIcon}
						</Avatar>

						{/* Content */}
						<Stack
							spacing={3}
							sx={{ flex: 1 }}>
							{/* Title and message */}
							<Stack spacing={1}>
								<Typography variant="h5">{title}</Typography>
								<Typography
									color="text.secondary"
									variant="body2">
									{displayMessage}
								</Typography>

								{/* Items preview */}
								{hasItemsPreview && (
									<>
										<Typography
											variant="subtitle2"
											color="text.primary"
											sx={{ fontWeight: 500, mt: 2 }}>
											{itemCount === 1
												? "Item details:"
												: `Items to be affected (${itemCount}):`}
										</Typography>
										<Box
											sx={{
												mt: 1,
												p: 2,
												bgcolor: "var(--mui-palette-background-default)",
												border: "1px solid var(--mui-palette-divider)",
												borderRadius: 1,
												maxHeight: 200,
												overflowY: "auto",
											}}>
											<Stack spacing={1}>
												{visibleItems.map((item) => (
													<Box key={item.id}>
														<Typography
															variant="body2"
															sx={{ fontWeight: 500 }}>
															{item.title}
														</Typography>
														{item.subtitle && (
															<Typography
																variant="caption"
																color="text.secondary">
																{item.subtitle}
															</Typography>
														)}
													</Box>
												))}
												{!showAllItems && hiddenItemsCount > 0 && (
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ fontStyle: "italic" }}>
														...and {hiddenItemsCount} more items
													</Typography>
												)}
											</Stack>
										</Box>
									</>
								)}

								{/* Custom content */}
								{children}
							</Stack>

							{/* Error display */}
							<Collapse in={!!error}>
								<Alert severity="error">{error}</Alert>
							</Collapse>

							{/* Actions */}
							<Stack
								direction="row"
								spacing={2}
								sx={{ justifyContent: "flex-end" }}>
								{allActions.map((action, index) => {
									const isPrimary = index === allActions.length - 1;
									const showLoading = loading && isPrimary && onConfirm;

									return (
										<Button
											key={index}
											color={action.color ?? "secondary"}
											variant={
												action.variant ?? (isPrimary ? "contained" : "text")
											}
											onClick={action.onClick}
											disabled={loading}
											sx={{
												...(isPrimary && {
													minWidth: 100,
												}),
											}}>
											{showLoading ? "Processing..." : action.label}
										</Button>
									);
								})}
							</Stack>
						</Stack>
					</Stack>
				</Paper>
			</Container>
		</Dialog>
	);
}
